/*
 * Setting the names inside a sentence the catalogue wrote — 15 Sep 2026.
 *
 * ── The problem this solves once ────────────────────────────────────────────────────────────────
 *
 * The brand kit says that where a name is used *as a name* it is set from the typeface's outlines
 * with the two e's joined by the accent bar, and the product owner extended that to both names
 * wherever either appears. But the strings the product shows are not names — they are sentences
 * with a name in them: "Ask seeldo", "seeldo replies", "seeld recruit". Splitting each of those by
 * hand at each call site would put the same three-line split in a dozen files and would put the
 * names themselves into JSX, which is the thing G-03 exists to stop.
 *
 * So there is one component, and it splits on the names **as the catalogue gives them**. Nothing
 * here spells either name: `brand.name` and `brand.assistant` are read at render time and used as
 * the needles, so a rename repoints this the same way it repoints `gates/brand-lint.mjs`.
 *
 * ── Why the longer name is matched first ────────────────────────────────────────────────────────
 *
 * One name is a prefix of the other. Matched in catalogue order, "seeldo" would be found as the
 * product's name followed by a stray letter, and the assistant would be set in the wrong outline
 * with the o hanging off it in the page's own font. Sorting by length is the whole fix and it is
 * one line, but it is the line that makes this correct rather than nearly correct.
 *
 * ── Where NOT to use it ─────────────────────────────────────────────────────────────────────────
 *
 * Running prose. The kit is explicit: the minimum lockup height is 24px, and a bar seven per cent
 * of the cap height reads as a smudge at body size rather than as the brand. This is for chrome —
 * a heading, a button, a panel's title — where the name is being used as a name.
 */

import { AssistantWord, Wordmark } from '@indovation/design-system';
import { useI18n } from '@indovation/i18n/react';
import type { ReactNode } from 'react';

export interface SetNameProps {
  /** A catalogue string that may contain either name. Rendered unchanged if it contains neither. */
  readonly text: string;
}

/**
 * Everything in the string that is not a name.
 *
 * Wrapped rather than left as a bare text node so a lockup can size the two halves against each
 * other — the brand kit's product lockup is the name plus the product's noun, and the two are not
 * the same size or the same weight. A caller that wants them identical simply styles nothing.
 */
function Plain({ children }: { children: string }) {
  return <span className="tf-set-name__text">{children}</span>;
}

export function SetName({ text }: SetNameProps) {
  const { t } = useI18n();

  const names = [
    { word: t('brand.assistant'), render: (key: string, label: string) => <AssistantWord key={key} label={label} /> },
    { word: t('brand.name'), render: (key: string, label: string) => <Wordmark key={key} label={label} /> },
  ]
    .filter((name) => name.word.length > 0)
    /* Longest first — see the header. One name is a prefix of the other. */
    .sort((a, b) => b.word.length - a.word.length);

  const parts: ReactNode[] = [];
  let rest = text;
  let guard = 0;

  while (rest.length > 0 && guard < 16) {
    guard += 1;
    const hit = names
      .map((name) => ({ name, at: rest.indexOf(name.word) }))
      .filter((found) => found.at !== -1)
      .sort((a, b) => a.at - b.at || b.name.word.length - a.name.word.length)[0];

    if (!hit) break;

    if (hit.at > 0) {
      parts.push(<Plain key={`t${guard}`}>{rest.slice(0, hit.at)}</Plain>);
    }
    parts.push(hit.name.render(`${guard}`, hit.name.word));
    rest = rest.slice(hit.at + hit.name.word.length);
  }

  if (parts.length === 0) return <>{text}</>;
  return (
    <>
      {parts}
      {rest.length > 0 ? <Plain>{rest}</Plain> : null}
    </>
  );
}
